import { motion } from 'motion/react';
import { Check, Shield, Zap, Users, Lock, Server, Code, Headphones, Building2, ArrowRight } from 'lucide-react';
import { useState } from 'react';
import { ContactSalesModal } from './ContactSalesModal';

export function Enterprise() {
  const [isContactSalesOpen, setIsContactSalesOpen] = useState(false);

  const enterpriseFeatures = [
    {
      icon: Users,
      title: 'Team Management & SSO',
      description: 'Admin dashboard, role-based access control, centralized billing, and single sign-on with Okta, Azure AD, or Google Workspace.',
      highlights: ['User provisioning', 'SCIM integration', 'Audit logs']
    },
    {
      icon: Shield,
      title: 'Enterprise Security & Compliance',
      description: 'SOC 2 Type 2 ready, data processing agreements, GDPR compliance, and custom data residency options.',
      highlights: ['DPA & BAA available', 'Data sovereignty', 'Penetration testing']
    },
    {
      icon: Server,
      title: 'On-Premise / Self-Hosted Deployment',
      description: 'Air-gapped deployment with no external API calls. Complete data sovereignty and control for regulated industries.',
      highlights: ['Private cloud option', 'Docker/Kubernetes', 'Full data isolation']
    },
    {
      icon: Code,
      title: 'Full API Access',
      description: 'RESTful API for bulk email generation, CRM integrations, custom workflows, and automated processes.',
      highlights: ['Bulk generation', 'Webhook support', 'Custom integrations']
    },
    {
      icon: Zap,
      title: 'Custom Model Training',
      description: 'Fine-tune Grok or OpenAI models on your internal data, communication style, and domain-specific language.',
      highlights: ['Proprietary training', 'Style adaptation', 'Industry terminology']
    },
    {
      icon: Headphones,
      title: 'Dedicated 24/7 Support',
      description: 'SLA-backed support with 1-hour response times, dedicated account manager, and priority feature requests.',
      highlights: ['Named support engineer', 'Quarterly reviews', 'Training sessions']
    }
  ];

  const comparisonFeatures = [
    { feature: 'Unlimited email drafts', ultimate: true, enterprise: true },
    { feature: 'Grok-4 Advanced', ultimate: true, enterprise: true },
    { feature: 'All content packages (lifetime)', ultimate: true, enterprise: true },
    { feature: 'Priority support', ultimate: true, enterprise: true },
    { feature: 'Advanced analytics', ultimate: true, enterprise: true },
    { feature: 'Team management & SSO', ultimate: false, enterprise: true },
    { feature: 'Full API access', ultimate: false, enterprise: true },
    { feature: 'On-premise deployment', ultimate: false, enterprise: true },
    { feature: 'Custom model training', ultimate: false, enterprise: true },
    { feature: 'SLA guarantees (1-hour response)', ultimate: false, enterprise: true },
    { feature: 'Dedicated account manager', ultimate: false, enterprise: true },
    { feature: 'Custom integrations (Outlook, CRM)', ultimate: false, enterprise: true },
    { feature: 'Compliance guarantees (SOC 2, DPA)', ultimate: false, enterprise: true }
  ];

  const useCases = [
    {
      title: 'Financial Services',
      description: 'Banks, investment firms, and insurance companies requiring air-gapped deployment and strict compliance.',
      needs: ['Data sovereignty', 'Regulatory compliance', 'Audit trails']
    },
    {
      title: 'Healthcare Organizations',
      description: 'Hospitals and health systems needing HIPAA compliance and secure patient communication workflows.',
      needs: ['HIPAA BAA', 'PHI protection', 'Secure messaging']
    },
    {
      title: 'Enterprise Sales Teams',
      description: 'Large B2B sales organizations with CRM integrations and custom email workflows at scale.',
      needs: ['Salesforce/HubSpot integration', 'Bulk generation', 'Team analytics']
    },
    {
      title: 'Recruiting Agencies',
      description: 'High-volume recruiting firms managing thousands of candidate communications daily.',
      needs: ['ATS integration', 'Candidate pipelines', 'Template management']
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 overflow-hidden backdrop-blur-sm">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '48px 48px' }} />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white/90 text-sm font-medium mb-6">
              <Building2 size={16} />
              Enterprise Solutions
            </div>
            
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6">
              AI Email Drafting<br />
              <span className="text-[#60A5FA]">Built for Enterprise</span>
            </h1>
            
            <p className="text-xl sm:text-2xl text-white/90 mb-8 max-w-3xl mx-auto">
              Custom pricing, dedicated support, and enterprise-grade security for teams and organizations that demand maximum control and scale.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => setIsContactSalesOpen(true)}
                className="px-8 py-4 bg-white text-[#1E3A8A] font-semibold rounded-lg hover:bg-white/90 transition-all shadow-xl hover:shadow-2xl flex items-center gap-2"
              >
                Contact Sales
                <ArrowRight size={20} />
              </button>
              <a
                href="/#pricing"
                className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-lg hover:bg-white/20 transition-all border-2 border-white/30"
              >
                Compare All Plans
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Enterprise Features Grid */}
      <section className="py-20 bg-gray-50/80 dark:bg-[#0F2847]/60 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-4">
              Enterprise-Grade Capabilities
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Everything you need for secure, scalable AI email drafting across your entire organization.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {enterpriseFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                className="p-8 bg-white dark:bg-[#1E3A8A]/10 rounded-2xl border-2 border-gray-200 dark:border-[#1E3A8A]/30 shadow-lg hover:shadow-xl transition-all"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="w-14 h-14 bg-[#60A5FA]/10 rounded-xl flex items-center justify-center mb-6">
                  <feature.icon size={28} className="text-[#60A5FA]" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  {feature.description}
                </p>
                <ul className="space-y-2">
                  {feature.highlights.map((highlight) => (
                    <li key={highlight} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <Check size={16} className="text-[#60A5FA] flex-shrink-0" />
                      {highlight}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Comparison Table */}
      <section className="py-20 bg-white/80 dark:bg-[#0F172A]/60 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-4">
              Ultimate vs Enterprise
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              See what makes Enterprise the right choice for your organization
            </p>
          </motion.div>

          <motion.div
            className="overflow-hidden rounded-2xl border-2 border-gray-200 dark:border-[#1E3A8A]/30 shadow-xl"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <table className="w-full">
              <thead className="bg-gray-100 dark:bg-[#1E3A8A]/10">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-white">
                    Feature
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900 dark:text-white">
                    Ultimate<br />
                    <span className="font-normal text-gray-600 dark:text-gray-400">$41-49/mo</span>
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900 dark:text-white">
                    Enterprise<br />
                    <span className="font-normal text-gray-600 dark:text-gray-400">Custom</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-[#0F172A]">
                {comparisonFeatures.map((item, index) => (
                  <tr
                    key={item.feature}
                    className={`border-t border-gray-200 dark:border-[#1E3A8A]/20 ${
                      index % 2 === 0 ? 'bg-gray-50/50 dark:bg-[#1E3A8A]/5' : ''
                    }`}
                  >
                    <td className="px-6 py-4 text-gray-900 dark:text-gray-300">
                      {item.feature}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {item.ultimate ? (
                        <Check size={20} className="text-green-500 mx-auto" />
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {item.enterprise ? (
                        <Check size={20} className="text-[#60A5FA] mx-auto" />
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 bg-gray-50/80 dark:bg-[#0F2847]/60 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-4">
              Built for Regulated Industries
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Trusted by organizations that can't compromise on security or compliance
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {useCases.map((useCase, index) => (
              <motion.div
                key={useCase.title}
                className="p-8 bg-white dark:bg-[#1E3A8A]/10 rounded-2xl border-2 border-gray-200 dark:border-[#1E3A8A]/30 shadow-lg"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                  {useCase.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  {useCase.description}
                </p>
                <div className="flex flex-wrap gap-2">
                  {useCase.needs.map((need) => (
                    <span
                      key={need}
                      className="px-3 py-1 bg-[#60A5FA]/10 text-[#60A5FA] dark:text-[#60A5FA] text-sm font-medium rounded-full"
                    >
                      {need}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Information */}
      <section className="py-20 bg-white/80 dark:bg-[#0F172A]/60 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-4">
              Custom Pricing for Your Needs
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              Flexible pricing based on team size, deployment model, and requirements
            </p>
          </motion.div>

          <motion.div
            className="bg-gradient-to-br from-[#1E3A8A] to-[#60A5FA] rounded-2xl p-8 text-white shadow-2xl"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="text-2xl font-bold mb-4">Typical Pricing Ranges</h3>
            <div className="space-y-4 mb-8">
              <div className="flex items-start gap-3">
                <Check size={20} className="flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold">Per-User Licensing</p>
                  <p className="text-white/80">$99–$499+ per user per month (volume discounts available)</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Check size={20} className="flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold">Annual Contracts</p>
                  <p className="text-white/80">$10,000–$50,000+ flat annual fees for unlimited users</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Check size={20} className="flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold">On-Premise / Custom Deployment</p>
                  <p className="text-white/80">One-time setup fees starting at $25,000 (includes training & migration)</p>
                </div>
              </div>
            </div>

            <div className="border-t border-white/20 pt-6">
              <h4 className="text-xl font-bold mb-3">How It Works</h4>
              <ol className="space-y-2 text-white/90">
                <li>1. <strong>Contact Sales</strong> — Tell us about your team size, use case, and requirements</li>
                <li>2. <strong>Custom Proposal</strong> — We'll create a tailored package with transparent pricing</li>
                <li>3. <strong>Demo & Onboarding</strong> — See xRepl.ai in action with a dedicated demo for your team</li>
                <li>4. <strong>Deploy & Scale</strong> — We handle setup, training, and ongoing support</li>
              </ol>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800 dark:from-gray-950 dark:to-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Ready to Transform Your Organization?
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Let's discuss how xRepl.ai Enterprise can streamline communication across your entire team.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => setIsContactSalesOpen(true)}
                className="px-8 py-4 bg-[#60A5FA] text-white font-semibold rounded-lg hover:bg-[#60A5FA]/90 transition-all shadow-lg shadow-[#60A5FA]/30 hover:shadow-xl flex items-center gap-2"
              >
                Contact Sales Team
                <ArrowRight size={20} />
              </button>
              <a
                href="/#pricing"
                className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-lg hover:bg-white/20 transition-all border-2 border-white/30"
              >
                View Standard Plans
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <ContactSalesModal isOpen={isContactSalesOpen} onClose={() => setIsContactSalesOpen(false)} />
    </div>
  );
}